/**
 * EmailScriptModal — 2026-02 Go-Live v1 (Copy Email Script).
 *
 * Opens a lightweight modal with the auto-rendered email script for
 * the current appraisal: To, CC, Subject, Body, and the list of PDF
 * attachments the operator should pull from their G Drive save folder.
 *
 * All fields are read-only — permanent edits land in
 * `backend/email_script_defaults.py` until the Phase-2 Script Audit
 * Studio ships. Operator tweaks the content in their email client
 * after pasting.
 *
 * Props:
 *   appraisalId · open · onClose
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { X, Mail, Copy, FileText, AlertCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const _api = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api` });
_api.interceptors.request.use((cfg) => {
  const t = localStorage.getItem('access_token');
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

function CopyField({ label, value, testId, multiline = false, hint = '' }) {
  const copy = async () => {
    if (!value) {
      toast.error(`${label} is empty — nothing to copy.`);
      return;
    }
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`${label} copied`);
    } catch {
      toast.error('Clipboard blocked by browser — select and copy manually.');
    }
  };
  return (
    <div className="space-y-1" data-testid={`email-field-${testId}`}>
      <div className="flex items-center justify-between">
        <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
          {label}
        </label>
        <button
          type="button"
          onClick={copy}
          disabled={!value}
          data-testid={`email-copy-${testId}`}
          className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded border border-slate-200 bg-white hover:bg-slate-50 disabled:opacity-40 transition-colors"
        >
          <Copy size={11} /> Copy {label.split(' ')[0]}
        </button>
      </div>
      {multiline ? (
        <pre
          className="w-full p-3 border border-slate-200 rounded-md text-sm bg-slate-50 whitespace-pre-wrap font-sans text-slate-800 min-h-[160px]"
          data-testid={`email-value-${testId}`}
        >{value || ''}</pre>
      ) : (
        <div
          className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm bg-slate-50 text-slate-800 font-mono-num break-all"
          data-testid={`email-value-${testId}`}
        >{value || <span className="italic text-slate-400">—</span>}</div>
      )}
      {hint && (
        <p className="text-[11px] text-amber-600 italic flex items-start gap-1">
          <AlertCircle size={11} className="shrink-0 mt-0.5" /> {hint}
        </p>
      )}
    </div>
  );
}

export default function EmailScriptModal({ appraisalId, open, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (!open || !appraisalId) return;
    setLoading(true);
    setErr('');
    _api.get(`/email-script/${appraisalId}`)
      .then((r) => setData(r.data))
      .catch((e) => {
        setErr(e?.response?.data?.detail || e.message || 'Failed to load email script');
      })
      .finally(() => setLoading(false));
  }, [open, appraisalId]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-slate-900/60 overflow-y-auto p-4"
      data-testid="email-script-modal"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-2xl w-full my-8"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <Mail size={18} className="text-[#0F4F1B]" />
            <h3 className="text-base font-semibold text-slate-800">Email Script</h3>
            {data?.status && (
              <span className="text-[10px] uppercase tracking-wider font-bold text-[#0F4F1B] bg-[#0F4F1B]/10 px-2 py-0.5 rounded">
                {data.status}
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="email-script-close"
            className="p-1 rounded hover:bg-slate-100 text-slate-500"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 space-y-4">
          {loading && (
            <div className="flex items-center gap-2 text-sm text-slate-500 py-6 justify-center">
              <Loader2 size={14} className="animate-spin" /> Loading script…
            </div>
          )}
          {err && (
            <div className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded p-3">
              {err}
            </div>
          )}
          {data && !loading && (
            <>
              <CopyField label="To" value={data.to} testId="to" />
              <CopyField
                label="CC"
                value={data.cc}
                testId="cc"
                hint={data.cc_hint || ''}
              />
              <CopyField label="Subject" value={data.subject} testId="subject" />
              <CopyField label="Body" value={data.body} testId="body" multiline />

              {/* Attachments — informational text list */}
              <div className="space-y-1" data-testid="email-attachments-block">
                <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                  Attachments to include
                </label>
                {data.attachments && data.attachments.length > 0 ? (
                  <ul className="space-y-1 bg-slate-50 border border-slate-200 rounded-md p-3">
                    {data.attachments.map((name) => (
                      <li
                        key={name}
                        className="text-sm text-slate-700 flex items-center gap-2 font-mono-num"
                        data-testid={`email-attachment-${name}`}
                      >
                        <FileText size={13} className="text-slate-400 shrink-0" />
                        {name}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-slate-400 italic px-3 py-2 bg-slate-50 border border-slate-200 rounded-md">
                    No attachments for this status
                  </p>
                )}
                <p className="text-[11px] text-slate-500 italic">
                  Save them from the "Save to Disk" dropdown above — the
                  filenames will match exactly.
                </p>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-slate-100 bg-slate-50 rounded-b-lg">
          <p className="text-[11px] text-slate-500 italic">
            Permanent script edits coming via Script Audit Studio — for
            now the templates live in <code className="font-mono bg-slate-200 px-1 rounded">backend/email_script_defaults.py</code>.
          </p>
        </div>
      </div>
    </div>
  );
}
